import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_QUERY_KEYS } from 'consts';
import { patchDocument } from '../requests';
import type {
  PatchDocumentRequestType,
  PatchDocumentResponseType,
} from '../types';

export type PostDocumentMutationType = Omit<
  PatchDocumentRequestType,
  'documentId'
>;

export const usePatchDocument = (
  documentId?: PatchDocumentRequestType['documentId'],
) => {
  const queryClient = useQueryClient();

  const queryKey = [API_QUERY_KEYS.document, { documentId }];

  /**
   * Handles the success response of the mutation.
   * It updates the query data with the new document details.
   * @param data - The response data from the patch request.
   */
  const onSuccess = async (data: PatchDocumentResponseType) => {
    queryClient.setQueryData(queryKey, (oldData: any) => ({
      ...oldData,
      ...data,
    }));

    await Promise.all([
      queryClient.refetchQueries([API_QUERY_KEYS.document, { documentId }]),
      queryClient.refetchQueries([
        API_QUERY_KEYS.documentsQueue,
        { documentId },
      ]),
      queryClient.refetchQueries([
        API_QUERY_KEYS.documentsVersions,
        { documentId },
      ]),
      queryClient.refetchQueries([
        API_QUERY_KEYS.documentComments,
        { documentId },
      ]),
    ]);
  };

  return useMutation({
    mutationFn: (data: PostDocumentMutationType) =>
      patchDocument({
        documentId: documentId!,
        ...data,
      }),
    onSuccess,
  });
};
